import { Box, Button, Grid, Heading, HStack, Stack, Text } from '@chakra-ui/react';
import { ServiceRecord } from 'api/types';
import { get, isEmpty, isNil, omit, omitBy } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { useCopyToClipboard } from 'usehooks-ts';

import InputCopyField from '../../../components/form-elements/InputCopyField';
import { ServiceRecordIcon } from '../../../components/Icons';
import Link from '../../../components/Link';
import { dateTimeTransformer, dateTransformerNoTimezone } from '../../../helpers/dateTransformer';
import { displayRow } from '../../../helpers/general';
import { ContentCard } from '../../../layout/ContentCard';
import { LanguageHighlight } from '../LanguageHighlight';
import ServiceRecordData from '../ServiceRecordData';

const Mercedes = (props: {
  record: ServiceRecord;
  isLoading: boolean;
  schema: any;
  workshopLanguage: string;
  canChangeVin?: boolean;
}) => {
  const [applyAnnimation, setApplyAnnimation] = useState(false);
  const { t } = useTranslation();
  const { isLoading, record, schema, workshopLanguage, canChangeVin } = props;
  const [value, copy] = useCopyToClipboard();
  const recordData = omitBy(record?.data, (value) => value === undefined || value === null) || {};

  const additionalNotes = get(record, 'additional_notes', '-');

  return (
    <ContentCard
      isLoading={isLoading}
      icon={<ServiceRecordIcon boxSize={10} />}
      minH="fit-content"
      header={`${t('service_record:header')}( ${record?.user_readable_id} )`}
      data-test-id="service-record-detail-card"
      contentDescription={
        <HStack justifyContent={'space-between'}>
          <Link to={`/app/op/workshops/${record?.workshop_id}`}>{t('service_record:workshops.title')}</Link>
          <Text>
            {t('forms:submitted_at.label', {
              date: dateTimeTransformer(get(record, 'submitted_at', '-')),
            })}
          </Text>
        </HStack>
      }
    >
      <Grid templateColumns="repeat(auto-fit, minmax(340px, 1fr))" gap={4} rowGap={8}>
        <Stack spacing={4}>
          <Stack spacing={2}>
            <Heading size={'xxs'}>{t('service_record:title')}</Heading>
            <InputCopyField
              copy={{
                currentCopiedValue: value,
                handleCopy: copy,
              }}
              field={{
                value: dateTimeTransformer(get(record?.vehicle, 'initial_registration_date', '-')),
                label: t('forms:registration_date.label'),
              }}
              aria-label={'wmi'}
            />
            <button onClick={() => setApplyAnnimation(true)}>
              <InputCopyField
                copy={{
                  currentCopiedValue: value,
                  handleCopy: copy,
                }}
                field={{
                  value: get(record?.vehicle, 'vin', '-'),
                  label: t('forms:vin.label'),
                }}
                customContent={
                  canChangeVin &&
                  ((record?.status === 500 && record?.status_reason === 501) || (record?.status === 500 && record?.status_reason === 599)) && (
                    <Button colorScheme={'red'} size="sm" as={NavLink} to={`/app/op/service-record/${record?.id}/vin-update`}>
                      {t('service_record:vin.update.button')}
                    </Button>
                  )
                }
                aria-label={'vin'}
              />
            </button>
            {workshopLanguage?.toLocaleLowerCase() !== 'de' && (
              <LanguageHighlight workshopLanguage={workshopLanguage} applyAnnimation={applyAnnimation} />
            )}
            <InputCopyField
              copy={{
                currentCopiedValue: value,
                handleCopy: copy,
                copyValue: dateTransformerNoTimezone(get(record, 'date', '-'), 'dd-MM-yyyy'),
              }}
              field={{
                value: dateTimeTransformer(get(record, 'date', '-')),
                label: t('forms:service_date.label'),
              }}
              aria-label={'service_date'}
            />

            <InputCopyField
              copy={{
                currentCopiedValue: value,
                handleCopy: copy,
              }}
              field={{
                value: get(record, 'workshop_reference', '-'),
                label: t('forms:workshop_reference.label.mercedes'),
              }}
              aria-label={'workshop_reference'}
            />

            <InputCopyField
              copy={{
                currentCopiedValue: value,
                handleCopy: copy,
                copyValue: get(record, 'mileage', '-'),
              }}
              field={{
                value: get(record, 'mileage', '-') + ' km',
                label: t('forms:mileage.label'),
              }}
              aria-label={'mileage'}
            />
            <InputCopyField
              copy={{
                currentCopiedValue: value,
                handleCopy: copy,
              }}
              field={{
                value: get(recordData, 'daimler_service_code', '-'),
                label: t('forms:service_code.label'),
              }}
              aria-label={'service_code'}
            />
            <InputCopyField
              copy={{
                currentCopiedValue: value,
                handleCopy: copy,
                copyValue: get(record.data, 'daimler_start_mileage', '-'),
              }}
              field={{
                value: get(record.data, 'daimler_start_mileage', '-') + ' km',
                label: t('forms:daimler_start_mileage.label'),
              }}
              aria-label={'daimler_start_mileage'}
            />
            <InputCopyField
              copy={{
                currentCopiedValue: value,
                handleCopy: copy,
                copyValue: get(record.data, 'daimler_remaining_mileage', '-'),
              }}
              field={{
                value: get(record.data, 'daimler_remaining_mileage', '-') + ' km',
                label: t('forms:daimler_remaining_mileage.label'),
              }}
              aria-label={'daimler_remaining_mileage'}
            />
            <InputCopyField
              copy={{
                currentCopiedValue: value,
                handleCopy: copy,
                copyValue: get(record.data, 'daimler_remaining_time', '-'),
              }}
              field={{
                value: get(record.data, 'daimler_remaining_time', '-') + ' ' + t('common:days'),
                label: t('forms:daimler_remaining_time.label'),
              }}
              aria-label={'daimler_remaining_time'}
            />
          </Stack>
        </Stack>
        {!isEmpty(schema) && (
          <Grid gap={4} alignSelf={'start'}>
            <Heading size={'xxs'}>{t('service_record:data')}</Heading>
            <ServiceRecordData recordData={omit(recordData, ['service_code'])} schema={schema} />

            {!isNil(additionalNotes) && (
              <Box ml="60px">
                {displayRow({
                  label: t('forms:additional_notes.label'),
                  content: additionalNotes,
                  isLoading,
                  contentOnNewLine: true,
                })}
              </Box>
            )}
          </Grid>
        )}
      </Grid>
    </ContentCard>
  );
};

export default Mercedes;
